import { useMemo, type ReactElement } from "react";
import { Radio } from "lucide-react";
import { WallViewHeader } from "@/app/routes/dashboard/components/WallBar";
import { EmptyState } from "@/app/routes/dashboard/components/ViewStates";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { openBrandPicker } from "@/app/routes/dashboard/components/kit/BrandPicker";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import { useMarketSignals } from "./api/useMarketSignals";
import { isOptimisticSignal } from "./api/signalsCache";
import { useFieldReports } from "./api/useFieldReports";
import { useOrganizationMembers } from "../settings/api/useOrganization";
import SignalsBoard, { type SignalsActions } from "./SignalsBoard";
import "./signals.css";

/** The shell mounts this with `dark` and `embedded`. The cockpit owns the theme and the
 *  page only ever renders embedded, so neither is read; the type keeps them so the
 *  mount compiles unchanged. */
type SignalsSurfaceProps = { dark?: boolean; embedded?: boolean };

/**
 * SignalsSurface: the live-data container for the Signals view.
 *
 * It mounts inside the Creative OS cockpit shell, which owns the theme and the
 * --co-* tokens the page is drawn in. All presentation and triage state live in
 * SignalsBoard; this file only wires the board's action interface to the real
 * mutations. Request/response shapes are untouched: every mutation call is
 * byte-identical to the previous surface.
 */
const SignalsSurface: (props: SignalsSurfaceProps) => ReactElement = function SignalsSurface() {
  const { canEdit } = usePermissions();
  const { selectedBrandId, selectedOrganizationId } = useWorkspaceStore();
  const brandId = selectedBrandId;

  // The signed-in person, for the phone deck's greeting only: the viewer is not
  // the curator, so no other line on the page names them.
  const viewerName = useAuthStore((s) => s.user?.name ?? "");

  const {
    signals,
    isLoading,
    isError,
    invalidate,
    createSignal,
    acceptSignal,
    updateSignal,
    dismissSignal,
    graduateSignal,
  } = useMarketSignals(brandId);

  // Reports load alongside the signals but never gate the board: an unread report is
  // context, not a blocker for triage.
  const { reports, isLoading: reportsLoading } = useFieldReports(brandId);

  // Attribution: member userId -> display name. Kept OUT of isLoading and its errors
  // are ignored on purpose; an unresolved id just renders no curator name.
  const membersQuery = useOrganizationMembers(selectedOrganizationId);
  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of membersQuery.data ?? []) map.set(m.userId, m.name);
    return map;
  }, [membersQuery.data]);

  const actions: SignalsActions = useMemo(
    () => ({
      create: (draft) => createSignal.mutate(draft),
      accept: (signal, validUntil, cbs, quiet) =>
        acceptSignal.mutate({ signalId: signal.id, validUntil, quiet }, cbs),
      decline: (signal, cbs, quiet) =>
        dismissSignal.mutate(quiet ? { signalId: signal.id, quiet } : signal.id, cbs),
      editPending: (signal, patch) => updateSignal.mutate({ signalId: signal.id, ...patch }),
      extend: (signal, validUntil) => updateSignal.mutate({ signalId: signal.id, validUntil }),
      dismissActive: (signal, cbs) => dismissSignal.mutate(signal.id, cbs),
      graduate: (signal) => graduateSignal.mutate(signal.id),
      // A signal this tab just created has no server id to act on until it lands.
      isBusy: (id) =>
        isOptimisticSignal(id) ||
        (acceptSignal.isPending && acceptSignal.variables?.signalId === id) ||
        (dismissSignal.isPending &&
          (typeof dismissSignal.variables === "string"
            ? dismissSignal.variables
            : dismissSignal.variables?.signalId) === id) ||
        (graduateSignal.isPending && graduateSignal.variables === id) ||
        (updateSignal.isPending && updateSignal.variables?.signalId === id),
    }),
    [createSignal, acceptSignal, updateSignal, dismissSignal, graduateSignal],
  );

  if (!brandId) {
    return (
      <div className="sig">
        <WallViewHeader title="Signals" subhead="No brand is selected in this workspace." />
        {/* Signals / No brand selected: inside the panel's inset, with Choose brand. */}
        <div className="sig-panels">
          <EmptyState
            align="start"
            className="sig-state"
            icon={<Radio aria-hidden />}
            title="No brand selected"
            body="Signals are read one brand at a time. Pick a brand from the workspace switcher to see what the observatory has found for it."
            actions={
              <KitButton onClick={openBrandPicker} className="h-10 sig-state__wide">
                Choose brand
              </KitButton>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <SignalsBoard
      signals={signals}
      reports={reports}
      isLoading={isLoading}
      isError={isError}
      onRetry={invalidate}
      reportsLoading={reportsLoading}
      viewerName={viewerName}
      canEdit={canEdit}
      memberNames={memberNames}
      actions={actions}
    />
  );
};

export default SignalsSurface;
